import { useApolloClient } from '@apollo/client';
import { uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useReportError } from '@/hooks/useReportError';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useSelectClientsForm } from '@/modules/household/ManageIntegrationDataModal/steps/selectClients/useSelectClientsForm';
import { IntegrationClientKind } from '@/types/schema';

import { useSalesforceBrand } from '../brand';
import {
  ResolveSalesforceHouseholdRecordDocument,
  ResolveSalesforceHouseholdRecordMutation,
  SalesforceHouseholdSuggestionsDocument,
  SalesforceHouseholdSuggestionsQuery,
  useSalesforceHouseholdDataQuery,
} from '../graphql/SalesforceHousehold.generated';
import { SalesforceHouseholdRecord } from '../householdLink';

export const MIN_SALESFORCE_SEARCH_LENGTH = 3;

export function useSalesforceHouseholdLink(householdId: string) {
  const client = useApolloClient();
  const brand = useSalesforceBrand();
  const household = useHouseholdDetailsContext();
  const defaultSearchTerms = useMemo(() => {
    if (household.householdId !== householdId) return [];
    const terms = (household.primaryClients ?? []).map(
      ({ lastName, displayName }) => {
        const surname = lastName.trim();
        return surname.length >= MIN_SALESFORCE_SEARCH_LENGTH
          ? surname
          : displayName.trim();
      }
    );
    return uniqBy(terms, (term) => term.toLowerCase()).filter(
      (term) => term.length >= MIN_SALESFORCE_SEARCH_LENGTH
    );
  }, [household.householdId, household.primaryClients, householdId]);
  const {
    data,
    loading,
    error: loadError,
    refetch,
  } = useSalesforceHouseholdDataQuery({
    variables: { householdId },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
  });
  const { reportError } = useReportError(
    'Failed to load Salesforce household link',
    loadError
  );
  const links = useMemo(
    () =>
      data?.households.edges?.flatMap(
        (edge) =>
          edge?.node?.integrationClients?.filter(
            ({ kind }) => kind === IntegrationClientKind.Salesforce
          ) ?? []
      ) ?? [],
    [data]
  );
  const link = links.length === 1 ? links[0] : null;
  const form = useSelectClientsForm(householdId, async (ids) => {
    if (!ids.length || ids[0] === link?.id) return ids;
    const { data: resolved } =
      await client.mutate<ResolveSalesforceHouseholdRecordMutation>({
        mutation: ResolveSalesforceHouseholdRecordDocument,
        variables: { householdId, recordId: ids[0] },
      });
    if (!resolved) throw new Error('Salesforce record resolution failed.');
    return [resolved.resolveSalesforceHouseholdRecord.id];
  });
  const { seed } = form;
  useEffect(() => {
    if (data) seed(links.map(({ id }) => id));
  }, [data, links, seed]);

  const [results, setResults] = useState<SalesforceHouseholdRecord[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const searchGeneration = useRef(0);
  const clearSearch = useCallback(() => {
    searchGeneration.current += 1;
    setSearching(false);
    setResults([]);
  }, []);
  useEffect(() => clearSearch, [clearSearch]);

  const search = useCallback(
    async (prefix: string | string[]) => {
      const terms = (typeof prefix === 'string' ? [prefix] : prefix)
        .map((term) => term.trim())
        .filter((term) => term.length >= MIN_SALESFORCE_SEARCH_LENGTH);
      if (!terms.length) {
        clearSearch();
        return;
      }
      const current = ++searchGeneration.current;
      setSearching(true);
      setError('');
      try {
        const suggestions = await Promise.all(
          terms.map((term) =>
            client.query<SalesforceHouseholdSuggestionsQuery>({
              query: SalesforceHouseholdSuggestionsDocument,
              variables: { householdId, prefix: term },
              fetchPolicy: 'no-cache',
            })
          )
        );
        if (current === searchGeneration.current)
          setResults(
            uniqBy(
              suggestions.flatMap(
                ({ data }) => data.salesforceHouseholdSuggestions
              ),
              'recordID'
            ).map(({ recordID, name, recordTypeName }) => ({
              id: recordID,
              name,
              recordTypeName: recordTypeName ?? undefined,
            }))
          );
      } catch (failure) {
        if (current !== searchGeneration.current) return;
        reportError(
          'Failed to search Salesforce household records',
          failure instanceof Error ? failure : undefined
        );
        setError(`Unable to search ${brand.name}. Try again.`);
      } finally {
        if (current === searchGeneration.current) setSearching(false);
      }
    },
    [brand, clearSearch, client, householdId, reportError]
  );

  async function save() {
    setError('');
    try {
      await form.save();
      clearSearch();
    } catch (failure) {
      reportError(
        'Failed to update Salesforce household link',
        failure instanceof Error ? failure : undefined
      );
      setError(`Unable to update the ${brand.name} link. Try again.`);
    }
  }

  let displayedError = error;
  if (loadError)
    displayedError = `Unable to load the ${brand.name} link. Try again.`;
  else if (links.length > 1)
    displayedError = `This household has multiple ${brand.name} selections.`;

  return {
    loading: loading && !data,
    connected: data?.salesforceHouseholdConnection.connected,
    objectApiName: data?.salesforceHouseholdConnection.object,
    link,
    formMethods: form.formMethods,
    defaultSearchTerms,
    results,
    searching,
    error: displayedError,
    loadFailed: Boolean(loadError),
    ambiguous: links.length > 1,
    retry: () =>
      refetch().catch((failure: Error) =>
        reportError('Failed to reload Salesforce household link', failure)
      ),
    search,
    clearSearch,
    save,
  };
}
